import { useIntl } from 'react-intl';
import { Divider, Form } from 'antd';

import { PageProps } from '@/types/PageProps';

import TabContentWrapper from '@/components/_common/TabContentWrapper';
import { SaveButton } from '@/components/_common/Buttons';
import ReturnMaterialForm from '@/components/Movements/ReturnOrDeliveryMaterial/ReturnMaterialForm';
import ReturnMaterialList from '@/components/Movements/ReturnOrDeliveryMaterial/ReturnMaterialList';
import {
  MaterialReturnListRequest,
  MaterialReturnRequest,
  ReturnMaterialFormValues,
  ReturnMaterialType,
} from '@/core/domain/stock/MaterialReturn/MaterialReturn.types';
import { useEffect, useState } from 'react';
import { AppPageHeader } from '@/components/_common';
import { useReturnMaterial } from '@/core/domain/stock/MaterialReturn/MaterialReturn.hooks';
import { DateTimeFormat } from '@/utils/moment';
import { useHistory } from 'react-router-dom';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import ButtonBack from '@/components/_common/ButtonBack';
import { useDebounce } from '@/hooks';
import { convertParams, usePersistSearchParams } from '@/utils/queryParams';
import ReturnMaterialFilterType from '@/components/Movements/ReturnOrDeliveryMaterial/ReturnMaterialList/ReturnMaterialFilterType';
import { useWorkorderConfigurationCompany } from '@/core/domain/workorder/WorkordersConfiguration/WorkordersConfiguration.hooks';

function ReturnMaterialCreatePage({ route }: PageProps) {
  const intl = useIntl();
  const history = useHistory();

  const [form] = Form.useForm<ReturnMaterialFormValues>();
  const [queryParams, setQueryParams] = useState<MaterialReturnListRequest>({
    order: 'DESC',
    type: ReturnMaterialType.part,
    ...convertParams<MaterialReturnListRequest>({
      companyId: 'number',
      search: 'string',
      type: 'number',
    }),
  });

  const [rowsSelected, setRowsSelected] = useState<string[]>([]);

  const queryDebounce = useDebounce(queryParams, 500);
  usePersistSearchParams(queryDebounce);

  const returnMaterialMutation = useReturnMaterial();

  const { data: config, mutateAsync: getConfig } =
    useWorkorderConfigurationCompany();

  const companyId = Form.useWatch('companyId', form);

  useEffect(() => {
    if (companyId) {
      (async () => {
        await getConfig(companyId);
        form.setFieldValue('deliveredItems', []);
      })();
    }
  }, [companyId]);

  const handleReturnMaterial = async () => {
    const values = await form.validateFields();

    if (rowsSelected) {
      const obj: MaterialReturnRequest = {
        companyId: values.companyId,
        receiverId: values.receiverId,
        returnerId: values.returnerId,
        returnDate: values.returnDate.format(DateTimeFormat),
        returnedItems: values.items
          .filter((item) => rowsSelected.includes(item.differentiator))
          .map((item) => ({
            complementId: item.complementId,
            complementItemId:
              item.type == ReturnMaterialType.part
                ? item.complementItemId
                : null,
            consumableId:
              item.type == ReturnMaterialType.consumable
                ? item.itemId
                : undefined,
            materialReturnId: item.itemId,
            returnedQuantity: item.returnedQuantity,
          })),
      };

      const response = await returnMaterialMutation.mutateAsync(obj);

      history.push(
        `/app/movements/deliveryOfMaterials/${response?.result?.id}/return`,
      );
    }
  };

  return (
    <TabContentWrapper
      title={route.name}
      permissions={route.permissions}
      extra={
        <SaveButton
          type="primary"
          onClick={handleReturnMaterial}
          disabled={!rowsSelected.length}
          loading={returnMaterialMutation.isLoading}
        >
          {intl.formatMessage({
            id: 'deliveryOrReturnOfMaterials.create.confirmedDevolution',
          })}
        </SaveButton>
      }
    >
      <Form form={form}>
        <ReturnMaterialForm
          queryParams={queryParams}
          setQueryParams={setQueryParams}
          form={form}
        />

        <Divider />

        <AppPageHeader
          onSearch={(value) =>
            setQueryParams((prev) => ({ ...prev, search: value }))
          }
          searchValue={queryParams.search}
        />

        <ReturnMaterialFilterType
          queryParams={queryParams}
          setQueryParams={setQueryParams}
        />

        <ReturnMaterialList
          config={config}
          params={queryParams}
          setRowsSelected={setRowsSelected}
          rowsSelected={rowsSelected}
        />
      </Form>

      <Divider />

      <SpaceForButton>
        <ButtonBack path="/app/movements/deliveryOfMaterials" />
      </SpaceForButton>
    </TabContentWrapper>
  );
}

export default ReturnMaterialCreatePage;
